import React, { useState } from 'react';
import api from '../api';
import { Barcode, Download, Printer, Loader2 } from 'lucide-react';

const BarcodeGenerator = () => {
  const [sku, setSku] = useState('');
  const [barcodeType, setBarcodeType] = useState('code128');
  const [barcodeImage, setBarcodeImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [_copied, _setCopied] = useState(false);
  const [batchSkus, setBatchSkus] = useState('');
  const [batchResults, setBatchResults] = useState([]);
  const [mode, setMode] = useState('single'); // single, batch, or printAll
  const [allBarcodes, setAllBarcodes] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allGenerated, setAllGenerated] = useState(false);

  const generateBarcode = async (skuValue) => {
    if (!skuValue.trim()) { alert('Please enter a SKU'); return null; }
    setLoading(true);
    try {
      const result = await api.generateBarcode(skuValue.trim(), barcodeType);
      return result;
    } catch (err) {
      alert('Failed to generate barcode: ' + err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleSingleGenerate = async () => {
    const result = await generateBarcode(sku);
    if (result) setBarcodeImage(result);
  };

  const handleBatchGenerate = async () => {
    const skus = batchSkus.split('\n').map(s => s.trim()).filter(Boolean);
    if (skus.length === 0) { alert('Enter at least one SKU'); return; }
    setLoading(true);
    const results = [];
    for (const s of skus) {
      try {
        const result = await api.generateBarcode(s, barcodeType);
        results.push(result);
      } catch (e) {
        results.push({ sku: s, error: e.message });
      }
    }
    setBatchResults(results);
    setLoading(false);
  };

  const handleDownload = (imageDataUrl, filename) => {
    const link = document.createElement('a');
    link.href = imageDataUrl;
    link.download = `barcode-${filename}.png`;
    link.click();
  };

  const handlePrint = (imageDataUrl, printerType = 'dymo', productName = '', sku = '') => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Popup blocked. Please allow popups for this site to print barcodes.');
      return;
    }
    if (printerType === 'dymo') {
      printWindow.document.write(`
        <html><head><title>Print Label - ${sku}</title>
        <style>
@page { size: 3.5in 1.125in; margin: 0; }
@media print { body { margin: 0; padding: 0; } }
* { box-sizing: border-box; }
body { width: 3.5in; height: 1.125in; margin: 0; padding: 0.06in 0.1in; display: flex; flex-direction: column; justify-content: center; font-family: Arial, Helvetica, sans-serif; overflow: hidden; }
.product-name { font-size: 8pt; font-weight: bold; line-height: 1.15; max-height: 0.28in; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-bottom: 0.02in; }
.barcode-row { display: flex; align-items: center; justify-content: center; }
img { max-width: 3.0in; max-height: 0.52in; height: 0.52in; object-fit: contain; }
.sku { font-family: 'Courier New', monospace; font-size: 7.5pt; text-align: center; margin-top: 0.02in; letter-spacing: 0.03em; }
        </style></head><body>
${productName ? `<div class="product-name">${productName.replace(/"/g, '&quot;').replace(/</g, '&lt;')}</div>` : ''}
<div class="barcode-row"><img src="${imageDataUrl}" /></div>
<div class="sku">${sku}</div>
<script>window.onload=function(){window.print();}</script>
        </body></html>
      `);
    } else {
      printWindow.document.write(`
        <html><head><title>Print Label - ${sku}</title>
        <style>
body { display:flex;flex-direction:column;justify-content:center;align-items:center;min-height:100vh;margin:0;font-family:Arial,sans-serif }
.label { width:3.5in;padding:0.2in;text-align:center;border:1px dashed #ccc }
.product-name { font-size:10pt;font-weight:bold;margin-bottom:6px }
img { max-width:3in;max-height:0.8in }
.sku { font-family:'Courier New',monospace;font-size:9pt;margin-top:4px;letter-spacing:0.05em }
@media print { .label { border:none } body { margin:0 } }
        </style></head><body>
<div class="label">
${productName ? `<div class="product-name">${productName.replace(/"/g, '&quot;').replace(/</g, '&lt;')}</div>` : ''}
<img src="${imageDataUrl}" />
<div class="sku">${sku}</div>
</div>
<script>window.onload=function(){window.print();}</script>
        </body></html>
      `);
    }
    printWindow.document.close();
  };

  const handleGenerateAll = async () => {
    setAllLoading(true);
    try {
      const result = await api.generateBulkBarcodes([]);
      setAllBarcodes(result.barcodes || []);
      setAllGenerated(true);
    } catch (err) {
      alert('Failed to generate barcodes: ' + err.message);
    } finally {
      setAllLoading(false);
    }
  };

  const [printAllType, setPrintAllType] = useState('dymo');

  const handlePrintAll = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Popup blocked. Please allow popups for this site to print barcodes.');
      return;
    }
    if (printAllType === 'dymo') {
      // Each label on its own Dymo 30252 page
      const labels = allBarcodes.map(bc => `
        <div class="label">
          <div class="product-name">${(bc.name || '').replace(/"/g, '&quot;').replace(/</g, '&lt;')}</div>
          <div class="barcode-row"><img src="${bc.image_data_url}" /></div>
          <div class="sku">${bc.sku}</div>
        </div>
      `).join('');
      printWindow.document.write(`
        <html><head><title>All Barcodes - Dymo</title>
        <style>
@page { size: 3.5in 1.125in; margin: 0; }
* { box-sizing: border-box; }
body { margin: 0; font-family: Arial, Helvetica, sans-serif; }
.label { width: 3.5in; height: 1.125in; padding: 0.06in 0.1in; display: flex; flex-direction: column; justify-content: center; overflow: hidden; page-break-after: always; }
.product-name { font-size: 8pt; font-weight: bold; line-height: 1.15; max-height: 0.28in; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-bottom: 0.02in; }
.barcode-row { display: flex; align-items: center; justify-content: center; }
img { max-width: 3.0in; max-height: 0.52in; height: 0.52in; object-fit: contain; }
.sku { font-family: 'Courier New', monospace; font-size: 7.5pt; text-align: center; margin-top: 0.02in; letter-spacing: 0.03em; }
        </style></head><body>${labels}</body></html>
      `);
    } else {
      // Regular printer - grid layout
      const cards = allBarcodes.map(bc => `
        <div style="border:1px solid #ccc;padding:8px;text-align:center;break-inside:avoid">
          <div style="font-size:9px;font-weight:bold;margin-bottom:3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${(bc.name || '').replace(/"/g, '&quot;').replace(/</g, '&lt;')}</div>
          <img src="${bc.image_data_url}" style="max-width:100%;height:auto;max-height:50px" />
          <div style="font-family:monospace;font-size:8px;margin-top:2px">${bc.sku}</div>
        </div>
      `).join('');
      printWindow.document.write(`
        <html><head><title>All Barcodes</title>
        <style>body{margin:16px;font-family:sans-serif}.grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}@media print{body{margin:8px}.grid{gap:4px}}</style>
        </head><body><div class="grid">${cards}</div></body></html>
      `);
    }
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  return (
    <div className="space-y-6" data-testid="barcode-generator-page">
      <div>
        <h2 className="text-3xl font-semibold tracking-tight">Barcode Generator</h2>
        <p className="text-muted-foreground">Generate barcodes for your products</p>
      </div>

      {/* Mode Tabs */}
      <div className="flex gap-2">
        <button onClick={() => setMode('single')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            mode === 'single' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
          }`} data-testid="mode-single">Single Barcode</button>
        <button onClick={() => setMode('batch')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            mode === 'batch' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
          }`} data-testid="mode-batch">Batch Generate</button>
        <button onClick={() => setMode('printAll')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            mode === 'printAll' ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
          }`} data-testid="mode-print-all">Print All Inventory</button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {mode === 'printAll' ? (
          <div className="col-span-2 space-y-4">
            <div className="bg-card p-6 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">Print All Inventory Barcodes</h3>
                  <p className="text-sm text-muted-foreground mt-1">Generate and print barcodes for every product with product names</p>
                </div>
                <div className="flex gap-3 items-center">
                  <select value={printAllType} onChange={(e) => setPrintAllType(e.target.value)}
                    className="px-3 py-2 border border-input rounded-md text-sm bg-background" data-testid="print-all-type-select">
                    <option value="dymo">Dymo 30252 Labels</option>
                    <option value="regular">Regular Printer</option>
                  </select>
                  <button onClick={handleGenerateAll} disabled={allLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
                    data-testid="generate-all-btn">
                    {allLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Barcode className="h-4 w-4" />}
                    {allLoading ? 'Generating...' : 'Generate All Barcodes'}
                  </button>
                  {allGenerated && (
                    <button onClick={handlePrintAll}
                      className="flex items-center gap-2 px-4 py-2 border border-input rounded-md hover:bg-muted"
                      data-testid="print-all-btn">
                      <Printer className="h-4 w-4" /> Print All ({allBarcodes.length})
                    </button>
                  )}
                </div>
              </div>
              {allGenerated && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[600px] overflow-y-auto">
                  {allBarcodes.map((bc, i) => (
                    <div key={i} className="border rounded-lg p-3 text-center bg-white" data-testid={`barcode-card-${i}`}>
                      <div className="text-xs font-bold truncate mb-1" title={bc.name}>{bc.name}</div>
                      <img src={bc.image_data_url} alt={bc.sku} className="mx-auto max-w-full h-auto" style={{maxHeight: '50px'}} />
                    </div>
                  ))}
                </div>
              )}
              {!allGenerated && !allLoading && (
                <div className="text-center py-12 text-muted-foreground">
                  <Barcode className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p>Click "Generate All Barcodes" to create printable barcodes for every product in inventory</p>
                </div>
              )}
            </div>
          </div>
        ) : (
        <>
        {/* Input */}
        <div className="bg-card p-6 rounded-lg border border-border">
          <h3 className="text-lg font-semibold mb-4">{mode === 'single' ? 'Enter SKU' : 'Enter SKUs (one per line)'}</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Barcode Type</label>
              <select value={barcodeType} onChange={(e) => setBarcodeType(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background" data-testid="barcode-type-select">
                <option value="code128">Code 128 (recommended)</option>
                <option value="code39">Code 39</option>
                <option value="ean13">EAN-13 (13 digits)</option>
                <option value="ean8">EAN-8 (8 digits)</option>
                <option value="upc">UPC-A (12 digits)</option>
              </select>
            </div>

            {mode === 'single' ? (
              <div>
                <label className="block text-sm font-medium mb-1">SKU / Product Code</label>
                <div className="flex gap-2">
                  <input type="text" value={sku} onChange={(e) => setSku(e.target.value)}
                    placeholder="Enter SKU..." onKeyDown={(e) => e.key === 'Enter' && handleSingleGenerate()}
                    className="flex-1 px-3 py-2 border border-input rounded-md" data-testid="sku-input" />
                  <button onClick={handleSingleGenerate} disabled={loading}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                    data-testid="generate-btn">
                    <Barcode className="h-4 w-4" /> {loading ? 'Generating...' : 'Generate'}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-1">SKUs (one per line)</label>
                <textarea value={batchSkus} onChange={(e) => setBatchSkus(e.target.value)}
                  placeholder="SKU-001\nSKU-002\nSKU-003" rows={8}
                  className="w-full px-3 py-2 border border-input rounded-md font-mono text-sm" data-testid="batch-skus" />
                <button onClick={handleBatchGenerate} disabled={loading}
                  className="mt-2 w-full px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
                  data-testid="batch-generate-btn">
                  {loading ? 'Generating...' : `Generate ${batchSkus.split('\n').filter(s => s.trim()).length} Barcodes`}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Output */}
        <div className="bg-card p-6 rounded-lg border border-border">
          <h3 className="text-lg font-semibold mb-4">Preview</h3>
          
          {mode === 'single' ? (
            barcodeImage ? (
              <div className="text-center space-y-4">
                <div className="bg-white p-4 rounded-lg border inline-block">
                  <img src={barcodeImage.image_data_url} alt={`Barcode for ${barcodeImage.sku}`}
                    className="max-w-full" data-testid="barcode-preview" />
                </div>
                <p className="text-sm font-mono text-muted-foreground">{barcodeImage.sku}</p>
                <div className="flex justify-center gap-2">
                  <button onClick={() => handleDownload(barcodeImage.image_data_url, barcodeImage.sku)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm border border-input rounded-md hover:bg-muted" data-testid="download-btn">
                    <Download className="h-3.5 w-3.5" /> Download
                  </button>
                  <button onClick={() => handlePrint(barcodeImage.image_data_url, 'dymo', '', barcodeImage.sku)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-primary/90" data-testid="print-dymo-btn">
                    <Printer className="h-3.5 w-3.5" /> Dymo Label
                  </button>
                  <button onClick={() => handlePrint(barcodeImage.image_data_url, 'regular', '', barcodeImage.sku)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm border border-input rounded-md hover:bg-muted" data-testid="print-regular-btn">
                    <Printer className="h-3.5 w-3.5" /> Regular Printer
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-muted-foreground">
                <p>Enter a SKU and click Generate</p>
              </div>
            )
          ) : (
            batchResults.length > 0 ? (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {batchResults.map((result, i) => (
                  <div key={i} className="bg-white p-3 rounded border flex items-center gap-4">
                    {result.error ? (
                      <p className="text-red-600 text-sm">Error: {result.sku} - {result.error}</p>
                    ) : (
                      <>
                        <img src={result.image_data_url} alt={result.sku} className="h-16" />
                        <span className="font-mono text-sm flex-1">{result.sku}</span>
                        <button onClick={() => handleDownload(result.image_data_url, result.sku)}
                          className="p-1.5 hover:bg-muted rounded"><Download className="h-4 w-4" /></button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-muted-foreground">
                <p>Enter SKUs and click Generate</p>
              </div>
            )
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default BarcodeGenerator;